<template>
  <h5 class="mb-3">{{ props.pensionPot.name }}</h5>

  <div class="form-floating mb-3">
    <number-input
      ref="currentValueInput"
      v-model="currentValueModel"
      description="value"
      :is-read-only="!props.pensionPot.userCanEditCurrentValue"
      :max="10000000"
      @entered="onSaveShortcut">
    </number-input>
  </div>

  <p>
    How much personal contribution is being paid in each month?
  </p>

  <div class="form-floating mb-3">
    <number-input
      ref="personalMonthlyContributionInput"
      v-model="personalContributionModel"
      description="personal monthly contribution"
      :is-read-only="!props.pensionPot.userCanEditContribution"
      :optional="true"
      :min="0"
      :max="20000"
      @entered="onSaveShortcut">
    </number-input>
  </div>

  <p>
    How much employer contribution being paid in each month?
  </p>

  <div class="form-floating mb-3">
    <number-input
      ref="employerMonthlyContributionInput"
      v-model="employerContributionModel"
      description="employer monthly contribution"
      :is-read-only="!props.pensionPot.userCanEditContribution"
      :optional="true"
      :min="0"
      :max="20000"
      @entered="onSaveShortcut">
    </number-input>
  </div>

  <p>We will assume these contribution amounts rise each year as your salary increases.</p>

  <bad-response-viewer ref="badResponseViewer" />

  <div class="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
    <lock-button v-if="!isInViewOnlyMode" :text="buttonText" :async-action="onSaveClicked"></lock-button>

    <button
      type="button"
      class="btn btn-secondary me-md-2"
      aria-label="Close"
      v-on:click="onCancelClicked"
    >
      {{ cancelButtonText }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref, computed, provide, watch } from "vue";
import { PensionPot } from "@/guiide/ts/pensionPot";
import NumberInput from "../common/NumberInput.vue";
import BadResponseViewer from "../../../shared/components/BadResponseViewer.vue";
import LockButton from "@/guiide/components/common/LockButton.vue";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Constants from "@/guiide/ts/constants";
import { app } from "@/guiide/ts/app";

const emit = defineEmits<{(event: 'edit-finished'): void}>();

const props = defineProps({
    pensionPot: { type: Object as PropType<PensionPot>, required: true },
});

const currentValueModel = ref<number | null>(0);
const personalContributionModel = ref<number | null>(0);
const employerContributionModel = ref<number | null>(0);
const currentValueInput = ref(null);
const personalMonthlyContributionInput = ref(null);
const employerMonthlyContributionInput = ref(null);
const badResponseViewer = ref(null);
const execute = ref(false);
provide('execute', execute);

watch(() => props.pensionPot, (newPensionPot) => {
  if (isNew(newPensionPot)) {
    currentValueModel.value = null;
    personalContributionModel.value = null;
    employerContributionModel.value = null;
  } else {
    currentValueModel.value = newPensionPot.valuationAmount;
    personalContributionModel.value = newPensionPot.personalMonthlyContribution;
    employerContributionModel.value = newPensionPot.employerMonthlyContribution;
  }
});

const buttonText = computed<string>(() => {
  return props.pensionPot.id === Constants.NewId ? "Add" : "Update";
});

const isInViewOnlyMode = computed<boolean>(() => {
  return !(props.pensionPot.userCanEditCurrentValue || props.pensionPot.userCanEditContribution);
});

const cancelButtonText = computed<string>(() => {
  return isInViewOnlyMode.value ? "Close" : "Cancel";
});

async function onSaveShortcut() {
  execute.value = true;
}

async function onSaveClicked() {
  if (!isValid()) {
    execute.value = false;
    return;
  }

  const willMakeFutureContribs = personalContributionModel.value > 0 || employerContributionModel.value > 0;

  const update = ObjectBuilder.createPensionPot(
    props.pensionPot.id,
    props.pensionPot.name,
    currentValueModel.value,
    0,
    0,
    0,
    willMakeFutureContribs,
    personalContributionModel.value,
    employerContributionModel.value,
    true,
    props.pensionPot.userCanEditCurrentValue,
    props.pensionPot.userCanEditContribution
  );

  const responseTracker = await app.updatePensionPot(update);

  execute.value = false;

  if (responseTracker.isOk) {
    editIsFinished();
  } else {
    if (responseTracker.trackerWillHandleFailure) {
      responseTracker.handleFailure();
    } else {
      badResponseViewer.value.update(
        responseTracker.isOk,
        responseTracker.message
      );
    }
  }
};

function isValid(): boolean {
  if (!currentValueInput.value.isValid()) {
    return false;
  }

  if (!personalMonthlyContributionInput.value.isValid()) {
    return false;
  }

  if (!employerMonthlyContributionInput.value.isValid()) {
    return false;
  }

  return true;
};

function editIsFinished() {
  currentValueInput.value.hideAnyWarnings();
  personalMonthlyContributionInput.value.hideAnyWarnings();
  employerMonthlyContributionInput.value.hideAnyWarnings();
  badResponseViewer.value.clear();
  emit("edit-finished");
};

function isNew(pensionPot: PensionPot): boolean {
  return pensionPot.id === Constants.NewId;
}

function onCancelClicked() {
  currentValueModel.value = props.pensionPot.valuationAmount;
  personalContributionModel.value = props.pensionPot.personalMonthlyContribution;
  employerContributionModel.value = props.pensionPot.employerMonthlyContribution;
  editIsFinished();
};
</script>

<style>

</style>
